import { useApolloClient } from '@apollo/client';
import { isObject } from 'lodash/fp';
import { useCallback } from 'react';
import { type LocalVariantSpecsFragment } from '../../../../api/fragments/LocalVariantSpecs';
import { type UploadFileWithPreviewFormDataFragment } from '../../../../api/fragments/UploadFileWithPreviewFormData';
import {
    DeleteVariantAssetDocument,
    type DeleteVariantAssetMutation,
    type DeleteVariantAssetMutationVariables,
} from '../../../../api/mutations/deleteVariantAsset';
import {
    UploadVariantAssetDocument,
    type UploadVariantAssetMutation,
    type UploadVariantAssetMutationVariables,
} from '../../../../api/mutations/uploadVariantAsset';
import { type UploadedFileWithPreview, VariantAsset } from '../../../../api/types';
import { type FormValues } from './types';

const useHandleVariantAssets = () => {
    const apolloClient = useApolloClient();

    return useCallback(
        (variantId: string, values: FormValues, initialValues?: LocalVariantSpecsFragment) => {
            const { images, brochure } = values;

            if (!images?.length) {
                return Promise.resolve([]);
            }

            const uploads = [];

            const imagesToUploadHaveIds = images.filter(
                (image: File | UploadFileWithPreviewFormDataFragment) => !(image instanceof File)
            ) as UploadedFileWithPreview[];
            const imageIdsToDelete = [];
            initialValues?.images.forEach(image => {
                if (image.id && !imagesToUploadHaveIds.find(imageToUpload => imageToUpload?.id === image.id)) {
                    imageIdsToDelete.push(image.id);
                }
            });

            images.forEach(picture => {
                if (picture instanceof File) {
                    uploads.push(
                        apolloClient.mutate<UploadVariantAssetMutation, UploadVariantAssetMutationVariables>({
                            mutation: UploadVariantAssetDocument,
                            variables: { variantId, type: VariantAsset.Picture, upload: picture },
                        })
                    );
                }
            });

            imageIdsToDelete.forEach(imageId => {
                uploads.push(
                    apolloClient.mutate<DeleteVariantAssetMutation, DeleteVariantAssetMutationVariables>({
                        mutation: DeleteVariantAssetDocument,
                        variables: { variantId, uploadId: imageId, type: VariantAsset.Picture },
                    })
                );
            });

            if (brochure instanceof File) {
                uploads.push(
                    apolloClient.mutate<UploadVariantAssetMutation, UploadVariantAssetMutationVariables>({
                        mutation: UploadVariantAssetDocument,
                        variables: { variantId, type: VariantAsset.Brochure, upload: brochure },
                    })
                );
            } else if (brochure === null && isObject(initialValues.brochure)) {
                // delete existing file
                uploads.push(
                    apolloClient.mutate<DeleteVariantAssetMutation, DeleteVariantAssetMutationVariables>({
                        mutation: DeleteVariantAssetDocument,
                        variables: { variantId, uploadId: brochure.id, type: VariantAsset.Brochure },
                    })
                );
            }
            if (uploads.length) {
                return Promise.all(uploads);
            }

            return Promise.resolve([]);
        },
        [apolloClient]
    );
};

export default useHandleVariantAssets;
